import Popover from '@mui/material/Popover';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FormLabel, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { wardPatients } from '../../store/reducers/ward';
import AgyleButton from '../ui/buttons/agyleButton';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import moment from 'moment';
import { hasOneOfPermission, Permissions, sendUserAudit, user } from '../../store/reducers/user';
import { setSelectPatientEvent } from '../../store/reducers/wardMap';
import { setSnackbar } from '../../store/reducers/globals';
import API from '../../app/api';
import useOpenState from '../../hooks/useOpenState';
import PostAddIcon from '@mui/icons-material/PostAdd';
import Tooltip from '@mui/material/Tooltip';
import MessageBox from '../../util/messageBox';
import { DEFAULT_STATE } from '../../pages/ward/forms/csm/bedRequestFormExternal';
import Formatter from '../../util/formatter';
import useCliniciansAndNursesDict from '../../hooks/useCliniciansAndNursesDict';
import { addNoteForPatient } from '../../store/reducers/notes';
import Notes from '../../util/notes';
import { loadAdditionalPatientData, openModal, prepareDataBM, setBedAllocateChosenPatient } from '../../store/reducers/bedManagement';
import { selectPatientForAppointment } from '../../store/reducers/appointments';
import { EventAvailable } from '@mui/icons-material';
import { AUDIT_TYPES } from '../../util/auditUtil';
import { clearSearchFilter } from '../../store/reducers/patientContext';
import { PATIENT_TAGS } from '../../util/patientUtil';
import { getConfig } from '../../util/configUtil';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Chip from '@mui/material/Chip';

const useStyles = makeStyles((theme) => ({
    paper: {
        border: '1px solid #ACAFB3',
        borderRadius: 8,
    },
    root: {
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#DEE1E5',
        width: '100%',
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    counter: {
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#3D3E40',
        borderRadius: 4,
        padding: 2,
        fontSize: 'small',
        marginLeft: 10,
        width: 25,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    right: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    ptCard: {
        backgroundColor: theme.palette.mode === 'dark' ? '#3D3E40' : '#FFFFFF',
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
        width: '100%',
        '&:hover': {
            backgroundColor: theme.palette.mode === 'light' ? '#EFF1F3' : '#4b4b4b',
        },
    },
    addBR: {
        backgroundColor: theme.palette.mode === 'dark' ? '#3D3E40' : '#FFFFFF',
        padding: 8,
        borderRadius: 8,
        height: 60,
    },
    familyName: {
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    gender: {
        marginLeft: 8,
        borderRadius: 6,
        color: '#222425',
        display: 'flex',
        padding: 2,
        '&.Male': {
            backgroundColor: '#92bfe2',
        },
        '&.Female': {
            backgroundColor: '#f4bdd1',
        },
    },
    secondLine: {
        gap: 8,
        fontSize: 'small',
    },
    subText: {
        color: theme.palette.mode === 'light' ? '#626466' : '#ACAFB3',
    },
    patientType: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100px',
        borderRadius: 5,
        height: '25px',
        lineHeight: '14px',
        fontWeight: 700,
        border: '1px solid transparent',
        paddingTop: 1,
        '&.Inpatient': {
            borderColor: '#4981B3',
            color: '#4981B3',
        },
        '&.TCI': {
            borderColor: '#B75BE2',
            color: '#B75BE2',
        },
        '&.ED': {
            borderColor: '#359F9F',
            color: '#359F9F',
        },
        '&.Other, &.Outpatient': {
            borderColor: '#e7ffff',
            color: '#E7FFFFFF',
        },
    },
    tempPtFlag: {
        marginLeft: 5,
        fontWeight: 500,
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.background.paperDarker : theme.palette.primary.mainColor,
        border: `1px solid ${theme.palette.mode === 'light' ? theme.palette.primary.mainColor : 'transparent'}`,
    },
}));

const GlobalSearchModal = ({
    anchorEl,
    handleClose,
    patients,
    currentWard,
    maxResults,
    disableMore,
    bedManagement,
    autoWidth,
    appointments,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();

    const open = Boolean(anchorEl);

    // Configs
    const defaultDateFormat = getConfig('defaultDateFormat');
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const dropdownsWithCliniciansAndNurses = getConfig('dropdownsWithCliniciansAndNurses');
    const inpatientEnabled = getConfig('modules.inpatient.enabled', false);

    // User object
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);
    const [nursesCliniciansList] = useCliniciansAndNursesDict('', !dropdownsWithCliniciansAndNurses);

    // Patients
    const wardPatientsObject = useSelector(wardPatients);
    const [patientsInWard, setPatientsInWard] = useState([]);
    const [patientsInHospital, setPatientsInHospital] = useState([]);
    const [patientsInDb, setPatientsInDb] = useState([]);

    // Create a bed request
    const tempPt = useRef(null);
    const tempBR = useRef(null);
    const [isCreateRequestOpen, openCreateRequest, closeCreateRequest] = useOpenState(false, true);
    const [isBrModalOpen, openBrModal, closeBrModal] = useOpenState(false, true);

    useEffect(() => {
        const ptWard = [];
        const ptHos = [];
        const ptDb = [];

        _.forEach(patients, (pt) => {
            if (_.isEmpty(pt?.activeAttendances)) {
                ptDb.push(pt);
            } else if (currentWard) {
                const isInWard = pt?.activeAttendances?.some((att) =>
                    wardPatientsObject?.some(
                        (currentPts) => currentPts.admission && currentPts.admission.attendanceId === att.attendanceId,
                    ),
                );
                if (isInWard) ptWard.push(pt);
                else ptHos.push(pt);
            } else {
                ptHos.push(pt);
            }
        });

        setPatientsInWard(ptWard);
        setPatientsInHospital(ptHos);
        setPatientsInDb(ptDb);
    }, [patients, currentWard]);

    const handleRedirectToPtCtx = (ptId) => history.push('/patient-context/' + ptId);

    const handleOpenPt = (pt, type, redirectToPtCtx = false) => {
        if (dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_OPEN_PATIENT_RECORD, Permissions.AGYLE_CAN_VIEW_ONLY]))) {
            // eslint-disable-next-line default-case
            switch (type) {
                case 'ward': {
                    let check = null;
                    _.forEach(pt?.activeAttendances, (att) => {
                        if (check) return;
                        check = _.find(wardPatientsObject, (wbPt) => wbPt?.admission?.attendanceId === att?.attendanceId);
                    });
                    if (check) {
                        if (!redirectToPtCtx) {
                            dispatch(
                                setSelectPatientEvent({
                                    patient: check,
                                }),
                            );
                        } else {
                            dispatch(sendUserAudit(check, AUDIT_TYPES.OPEN_PATIENT_CONTEXT));
                            handleRedirectToPtCtx(check?.patient?.__id);
                        }
                    }
                    break;
                }
                case 'hospital': {
                    const currentAttendance = pt?.activeAttendances?.[0];
                    const isIP = currentAttendance?.attendanceType === 'I';
                    if (!inpatientEnabled && isIP) {
                        dispatch(sendUserAudit({ patient: pt }, AUDIT_TYPES.OPEN_PATIENT_CONTEXT));
                        handleRedirectToPtCtx(pt?.__id);
                        break;
                    }
                    API.findPatientInWard({ visitNumber: pt.activeAttendances[0].attendanceId })
                        .then((res) => {
                            if (res?.data) {
                                if (redirectToPtCtx) {
                                    dispatch(sendUserAudit(res?.data, AUDIT_TYPES.OPEN_PATIENT_CONTEXT));
                                    handleRedirectToPtCtx(res?.data?.patient?.__id);
                                } else {
                                    dispatch(
                                        setSelectPatientEvent({
                                            patient: res?.data,
                                        }),
                                    );
                                }
                            } else {
                                dispatch(sendUserAudit({ patient: pt }, AUDIT_TYPES.OPEN_PATIENT_CONTEXT));
                                handleRedirectToPtCtx(pt?.__id);
                            }
                        })
                        .catch((err) => {
                            console.error(err);
                            dispatch(
                                setSnackbar({
                                    type: 'error',
                                    message: t('Unable to retrieve patient information. Please try again.'),
                                }),
                            );
                        });
                    break;
                }
                case 'db': {
                    dispatch(sendUserAudit({ patient: pt }, AUDIT_TYPES.OPEN_PATIENT_CONTEXT));
                    handleRedirectToPtCtx(pt.__id);
                    break;
                }
                case 'addAppointment': {
                    dispatch(selectPatientForAppointment(pt, false));
                    handleClose();
                    break;
                }
            }
        } else {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: t('You do not have permission to open patient records.'),
                }),
            );
        }
    };

    const getGenderIcon = (gender) => {
        switch (gender) {
            case 'Male':
                return <MaleIcon fontSize="small" />;
            case 'Female':
                return <FemaleIcon fontSize="small" />;
            case 'Unspecified':
                return <QuestionMarkIcon />;
            default:
                return null;
        }
    };

    const handleCreateBedRequests = (pt) => {
        if (pt?.tags?.includes(PATIENT_TAGS.TEMPORARY)) {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: t('Please merge patient record before creating bed request for temporary patient.'),
                }),
            );
        } else if (pt?.dod) {
            dispatch(setSnackbar({ type: 'warning', message: t('Unable to create bed request for deceased patient.') }));
        } else {
            API.getBedRequest(`status eq 'NEW' and patientId eq '${pt?.__id}'`)
                .then((res) => {
                    if (res || !_.isEmpty(res?.__data)) {
                        dispatch(
                            setSnackbar({
                                type: 'warning',
                                message: t('Patient already has an active bed request.'),
                            }),
                        );
                    } else {
                        tempPt.current = pt;
                        openCreateRequest();
                    }
                })
                .catch((err) => {
                    console.error(err);
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: t('Unable to retrieve bed requests. Please try again.'),
                        }),
                    );
                });
        }
    };

    const getPtType = (pt) => {
        const firstAtt = pt?.activeAttendances?.[0];
        if (firstAtt) {
            let text;
            switch (firstAtt?.attendanceType) {
                case 'I':
                    text = 'Inpatient';
                    break;
                case 'E':
                    text = 'ED';
                    break;
                case 'O':
                    text = 'Outpatient';
                    break;
                default:
                    text = 'Other';
                    break;
            }

            if (moment(firstAtt.startDate).isAfter(moment())) {
                text = 'TCI';
            }

            return <div className={clsx(classes.patientType, text)}>{text}</div>;
        }
        return null;
    };

    const getIsPtTemporary = (pt) => {
        const isTmp = pt?.tags?.includes(PATIENT_TAGS.TEMPORARY);

        return isTmp && <Chip label="Temporary" className={classes.tempPtFlag} size="small" />;
    };

    const renderPtList = (pt, index, type) => {
        if (index > maxResults - 1) return null;

        return (
            <div className={classes.divRow} style={{ gap: 5 }} key={pt?.__id}>
                <div className={classes.ptCard} onClick={handleOpenPt.bind(this, pt, type, appointments)}>
                    <div className={classes.divRow} style={{ gap: 5 }}>
                        <span className={classes.familyName}>{pt?.familyName},</span>

                        <span>{pt?.firstName}</span>

                        {pt?.gender && <div className={clsx(classes.gender, pt?.gender)}>{getGenderIcon(pt.gender)}</div>}

                        {getIsPtTemporary(pt)}

                        <div style={{ marginLeft: 'auto' }}>{getPtType(pt)}</div>
                    </div>

                    <div className={clsx(classes.divRow, classes.secondLine)}>
                        <span className={classes.subText}>{t('Hospital No')}:</span>

                        <span>{pt?.hospitalNumber || '-'}</span>

                        <span className={classes.subText}>{t('NHS No')}:</span>

                        <span>{pt?.nhsNumber || '-'}</span>

                        <span className={classes.subText}>{t('DOB')}:</span>

                        <span>{getAge(pt)}</span>

                        {pt.dod && (
                            <>
                                <span className={classes.subText}>{t('DOD')}:</span>

                                <span>{moment(pt.dod).format(defaultDateTimeFormat)}</span>
                            </>
                        )}
                    </div>
                </div>
                {bedManagement && type === 'hospital' && (
                    <div className={classes.addBR}>
                        <Tooltip title={t('Create Bed Request')}>
                            <IconButton onClick={() => handleCreateBedRequests(pt)}>
                                <PostAddIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}

                {appointments && (
                    <div className={classes.addBR}>
                        <Tooltip title={t('Create Appointment')}>
                            <IconButton onClick={handleOpenPt.bind(this, pt, 'addAppointment')}>
                                <EventAvailable />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    };

    const getAge = (pt) => {
        if (pt?.dob) {
            let ageAsText = '';
            const age = moment.duration(moment().diff(moment(pt?.dob))).asYears();
            if (age < 1) {
                const age = moment.duration(moment().diff(moment(pt?.dob))).asMonths();
                ageAsText = Math.floor(age) + ' m';
            } else {
                ageAsText = Math.floor(age) + ' y.o';
            }
            return `${moment(pt.dob).format(defaultDateFormat)} ${ageAsText}`;
        }
        return t('UNk');
    };

    const goToSearch = () => {
        dispatch(clearSearchFilter());
        history.push('/search');
    };

    const handleCloseCreateBR = () => {
        closeCreateRequest();
        tempPt.current = null;
    };

    const handleCloseBrModal = () => {
        closeBrModal();
        tempBR.current = null;
    };

    const handleOpenBr = async () => {
        if (tempBR.current) {
            let req;
            await API.getPatientsWithBedRequest({}).then(async (res) => {
                if (!_.isEmpty(res.__data)) {
                    const filtered = _.filter(
                        res.__data,
                        (br) => br.patient?.__id === tempBR.current?.patientId && br.admission?.__id === tempBR.current?.attendanceId,
                    );
                    const piwBR = await dispatch(prepareDataBM(filtered, true));
                    req = piwBR[0];
                }
            });
            if (req) {
                dispatch(setBedAllocateChosenPatient({ patient: req, request: req?.singleBedRequest }));
                dispatch(loadAdditionalPatientData(req?.patient?.__id));
                dispatch(openModal('requestScreen'));
            }
        }
        handleCloseBrModal();
    };

    const handleCreateNewBedRequest = () => {
        let br = _.cloneDeep(DEFAULT_STATE);
        const clinicianUser = _.find(nursesCliniciansList, { userName: userObject?.userName });
        const firstAtt = tempPt.current?.activeAttendances?.[0];

        API.findPatientInWard({ visitNumber: firstAtt.attendanceId })
            .then((res) => {
                if (res?.data) {
                    const ptId = tempPt.current?.__id;

                    // pre populate bed request form
                    let obj = br.requested;
                    obj.checked = true;
                    obj.when = moment();
                    obj.timestamp = moment().toISOString();
                    obj.user = userFullName;
                    obj.login = userObject?.userName;
                    obj.who = clinicianUser || null;

                    br.patientId = ptId;
                    br.attendanceId = res.data.admission.__id;
                    br.visitNumber = res.data.admission.attendanceId;
                    br.currentWardId = res.data.wardId;

                    API.saveBedRequest(br)
                        .then((brRes) => {
                            dispatch(
                                addNoteForPatient(
                                    ptId,
                                    {
                                        note: Notes.getBedRequestExternalNote(brRes),
                                        noteType: 'bedRequest',
                                        patientsInWardsRef: [res?.data?.__id],
                                        episodeId: res?.data?.admission?.consultantEpisodeId || null,
                                    },
                                    res?.data?.__id,
                                ),
                            );
                            dispatch(
                                setSnackbar({
                                    type: 'success',
                                    message: t('Bed request saved successfully.'),
                                }),
                            );
                            tempBR.current = brRes;
                            openBrModal();
                        })
                        .catch(() => {
                            dispatch(
                                setSnackbar({
                                    type: 'error',
                                    message: t('Failed to save bed request. Please try again.'),
                                }),
                            );
                        });
                } else {
                    dispatch(
                        setSnackbar({
                            type: 'warning',
                            message: t('Unable to find patient record. Please try again.'),
                        }),
                    );
                }
            })
            .catch((err) => {
                console.error(err);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: t('Failed to retrieve the patient details. Please try again later.'),
                    }),
                );
            })
            .finally(() => handleCloseCreateBR());
    };

    return (
        <>
            <Popover
                classes={{
                    paper: classes.paper,
                }}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        width: anchorEl && !autoWidth ? anchorEl.clientWidth : 'auto',
                    },
                }}
            >
                <div className={classes.root}>
                    {currentWard && (
                        <>
                            <div className={classes.divRow}>
                                <FormLabel>{t('Patients Currently Admitted to Location')}</FormLabel>

                                <div className={classes.counter}>{patientsInWard?.length || 0}</div>
                            </div>

                            {patientsInWard.map((pt, index) => renderPtList(pt, index, 'ward'))}
                        </>
                    )}

                    <div className={classes.divRow}>
                        <FormLabel>{t('Patients in Other Location')}</FormLabel>

                        <div className={classes.counter}>{patientsInHospital?.length || 0}</div>
                    </div>

                    {patientsInHospital.map((pt, index) => renderPtList(pt, index, 'hospital'))}

                    <div className={classes.divRow}>
                        <FormLabel>{t('Patient Records in Database')}</FormLabel>

                        <div className={classes.counter}>{patientsInDb?.length || 0}</div>
                    </div>

                    {patientsInDb.map((pt, index) => renderPtList(pt, index, 'db'))}

                    {patientsInDb?.length > 3 && !disableMore && (
                        <div className={classes.right}>
                            <AgyleButton size="small" variant="text" colour="primary" onClick={goToSearch}>
                                {t('See more results')}
                            </AgyleButton>
                        </div>
                    )}
                </div>
            </Popover>

            <MessageBox
                open={isCreateRequestOpen}
                onClose={handleCloseCreateBR}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={handleCreateNewBedRequest}
                message={t('Are you sure you want to create bed request for this patient?')}
            />

            <MessageBox
                open={isBrModalOpen}
                onClose={handleCloseBrModal}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                onAccept={handleOpenBr}
                message={t('Do you want to open the created bed request?')}
            />
        </>
    );
};

export default GlobalSearchModal;
